/** Shared UI pieces.
 *
 * The flow strip is the one element used in two places on purpose: it is the
 * editor in the builder and the live telemetry header on a running process,
 * so the thing you drew is literally the thing you watch.
 */

import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AppManifest, NormRect, ProcessGraph, ProcessSummary, RunState, api, session } from "../lib/api";
import { bytes, duration, relative } from "../lib/format";
import { BrandGlyph, Icon, IconName } from "./icons";

// ---------------------------------------------------------------- shell

const NAV: { group: string; items: { to: string; label: string; end?: boolean; icon: IconName }[] }[] = [
  { group: "Monitor", items: [
    { to: "/", label: "Overview", end: true, icon: "overview" },
    { to: "/processes", label: "Processes", icon: "processes" },
    { to: "/runs", label: "Runs", icon: "runs" },
    { to: "/events", label: "Events", icon: "events" },
  ]},
  { group: "Configure", items: [
    { to: "/holopods", label: "HoloPods", icon: "holopods" },
    { to: "/apps", label: "AI Apps", icon: "apps" },
    { to: "/models", label: "Models", icon: "models" },
    { to: "/sync", label: "Sync Sessions", icon: "sync" },
  ]},
  { group: "Data & Compute", items: [
    { to: "/data", label: "Data", icon: "data" },
    { to: "/compute", label: "Compute", icon: "compute" },
    { to: "/settings", label: "Settings", icon: "settings" },
  ]},
];

const CRUMB_LABELS: Record<string, string> = {
  "": "Overview",
  processes: "Processes",
  new: "New",
  edit: "Edit",
  runs: "Runs",
  holopods: "HoloPods",
  apps: "AI Apps",
  models: "Models",
  sync: "Sync Sessions",
  data: "Data",
  compute: "Compute",
  events: "Events",
  settings: "Settings",
};

export function BrandMark({ size = 16 }: { size?: number }) {
  return (
    <span className="brand-mark" aria-hidden>
      <BrandGlyph size={size} />
    </span>
  );
}

export function Shell({ children, workspaces, activeWorkspace, onWorkspaceChange, runningCount }: {
  children: ReactNode;
  workspaces: { id: string; name: string }[];
  activeWorkspace: string;
  onWorkspaceChange: (id: string) => void;
  runningCount: number;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(() => localStorage.getItem("holoxi.theme") ?? "dark");
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("holoxi.theme", theme);
  }, [theme]);

  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const crumbs = useMemo(() => breadcrumbs(location.pathname), [location.pathname]);
  const activeName = workspaces.find((workspace) => workspace.id === activeWorkspace)?.name ?? "Workspace";
  const shortcut = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl K";

  return (
    <div className="shell">
      {navOpen && <div className="nav-scrim" onClick={() => setNavOpen(false)} />}
      <nav className={`nav${navOpen ? " open" : ""}`}>
        <div className="brand">
          <BrandMark size={16} />
          <div>
            <div className="brand-name">HOLOXI</div>
            <div className="brand-tag">Live processes</div>
          </div>
          <button className="nav-close" aria-label="Close menu" onClick={() => setNavOpen(false)}>
            <Icon name="close" size={16} />
          </button>
        </div>

        <WorkspacePicker
          workspaces={workspaces}
          activeId={activeWorkspace}
          activeName={activeName}
          onChange={onWorkspaceChange}
        />

        <div className="nav-scroll">
          {NAV.map((section) => (
            <div className="nav-group" key={section.group}>
              <div className="nav-label">{section.group}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
                >
                  <Icon name={item.icon} size={16} />
                  {item.label}
                  {item.to === "/processes" && runningCount > 0 && (
                    <span className="nav-count">{runningCount}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        <div className="nav-footer">
          <button className="nav-item" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
            <Icon name={theme === "dark" ? "sun" : "moon"} size={16} />
            {theme === "dark" ? "Light theme" : "Dark theme"}
          </button>
          <button
            className="nav-item"
            onClick={() => { session.clear(); navigate("/login"); window.location.reload(); }}
          >
            <Icon name="logout" size={16} />
            Sign out
          </button>
        </div>
      </nav>

      <main className="main">
        <header className="topbar">
          <button className="nav-toggle" aria-label="Open menu" onClick={() => setNavOpen(true)}>
            <Icon name="menu" size={16} />
          </button>
          <nav className="crumbs" aria-label="Breadcrumb">
            {crumbs.map((crumb, index) => (
              <span key={`${crumb.to ?? ""}-${crumb.label}-${index}`} className="row" style={{ gap: 6 }}>
                {index > 0 && <span className="sep"><Icon name="chevron" size={12} /></span>}
                {crumb.to && index < crumbs.length - 1
                  ? <Link to={crumb.to}>{crumb.label}</Link>
                  : <span className="current">{crumb.label}</span>}
              </span>
            ))}
          </nav>
          {runningCount > 0 && (
            <Link to="/processes" className="live-pill">
              <span className="led running" />
              {runningCount} live
            </Link>
          )}
          <button className="search-trigger" onClick={() => setPaletteOpen(true)}>
            <Icon name="search" size={14} />
            <span>Search</span>
            <span className="spacer" />
            <kbd className="kbd">{shortcut}</kbd>
          </button>
        </header>
        {children}
      </main>

      {paletteOpen && (
        <CommandPalette
          onClose={() => setPaletteOpen(false)}
          runningCount={runningCount}
        />
      )}
    </div>
  );
}

function breadcrumbs(pathname: string): { label: string; to?: string }[] {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return [{ label: "Overview", to: "/" }];
  const crumbs: { label: string; to?: string }[] = [{ label: "Overview", to: "/" }];
  let path = "";
  parts.forEach((part, index) => {
    path += `/${part}`;
    const known = CRUMB_LABELS[part];
    const label = known ?? (part.length > 18 ? `${part.slice(0, 10)}…` : part.replace(/_/g, " "));
    crumbs.push({ label, to: index === parts.length - 1 ? undefined : path });
  });
  return crumbs;
}

function WorkspacePicker({ workspaces, activeId, activeName, onChange }: {
  workspaces: { id: string; name: string }[];
  activeId: string;
  activeName: string;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);

  if (workspaces.length <= 1) {
    return (
      <div className="ws-trigger" style={{ cursor: "default" }} title={activeName}>
        <span className="led online" />
        <span className="ws-name">{activeName}</span>
      </div>
    );
  }

  return (
    <div ref={root} style={{ position: "relative" }}>
      <button className="ws-trigger" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="led online" />
        <span className="ws-name">{activeName}</span>
        <Icon name="chevronDown" size={14} />
      </button>
      {open && (
        <div className="ws-menu" role="listbox">
          {workspaces.map((workspace) => (
            <button
              key={workspace.id}
              className={`ws-option${workspace.id === activeId ? " active" : ""}`}
              onClick={() => { onChange(workspace.id); setOpen(false); }}
            >
              {workspace.id === activeId && <Icon name="check" size={14} />}
              {workspace.id !== activeId && <span style={{ width: 14 }} />}
              {workspace.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CommandPalette({ onClose, runningCount }: { onClose: () => void; runningCount: number }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [processes, setProcesses] = useState<ProcessSummary[]>([]);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    input.current?.focus();
    api.processes().then(setProcesses).catch(() => undefined);
  }, []);

  const pages = NAV.flatMap((section) => section.items).map((item) => ({
    id: item.to,
    label: item.label,
    hint: sectionHint(item.to),
    icon: item.icon,
    run: () => navigate(item.to),
  }));

  const actions = [
    { id: "new-process", label: "New process", hint: "Create", icon: "plus" as IconName, run: () => navigate("/processes/new") },
    { id: "add-source", label: "Add a HoloPod", hint: "Source", icon: "holopods" as IconName, run: () => navigate("/holopods") },
  ];

  const q = query.trim().toLowerCase();
  const match = (label: string, hint?: string) =>
    !q || label.toLowerCase().includes(q) || (hint ?? "").toLowerCase().includes(q);

  const groups = [
    { title: "Actions", items: actions.filter((item) => match(item.label, item.hint)) },
    { title: "Pages", items: pages.filter((item) => match(item.label, item.hint)) },
    {
      title: "Processes",
      items: processes.filter((process) => match(process.name)).slice(0, 8).map((process) => ({
        id: process.id,
        label: process.name,
        hint: process.active_run ? "running" : "idle",
        icon: "processes" as IconName,
        run: () => navigate(`/processes/${process.id}`),
      })),
    },
  ].filter((group) => group.items.length > 0);

  const flat = groups.flatMap((group) => group.items);

  useEffect(() => { setActive(0); }, [query]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") { onClose(); return; }
      if (event.key === "ArrowDown") { event.preventDefault(); setActive((value) => Math.min(flat.length - 1, value + 1)); }
      if (event.key === "ArrowUp") { event.preventDefault(); setActive((value) => Math.max(0, value - 1)); }
      if (event.key === "Enter") {
        event.preventDefault();
        const item = flat[active];
        if (item) { item.run(); onClose(); }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [active, flat, onClose]);

  let cursor = -1;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="cmdk" onClick={(event) => event.stopPropagation()} role="dialog" aria-label="Search">
        <div className="cmdk-input">
          <Icon name="search" size={16} />
          <input
            ref={input}
            type="text"
            placeholder={runningCount > 0 ? `Search pages, processes…  ${runningCount} live` : "Search pages, processes…"}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <kbd className="kbd">esc</kbd>
        </div>
        <div className="cmdk-list">
          {flat.length === 0 && <div className="cmdk-empty">Nothing matches that search.</div>}
          {groups.map((group) => (
            <div key={group.title}>
              <div className="cmdk-group">{group.title}</div>
              {group.items.map((item) => {
                cursor += 1;
                const index = cursor;
                return (
                  <button
                    key={item.id}
                    className={`cmdk-item${index === active ? " active" : ""}`}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => { item.run(); onClose(); }}
                  >
                    <Icon name={item.icon} size={15} />
                    {item.label}
                    {item.hint && <span className="hint">{item.hint}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function sectionHint(to: string) {
  if (to === "/") return "Monitor";
  if (["/processes", "/runs", "/events"].includes(to)) return "Monitor";
  if (["/holopods", "/apps", "/models", "/sync"].includes(to)) return "Configure";
  return "Data & Compute";
}

export function PageHeader({ title, description, actions }: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function SearchField({ value, onChange, placeholder }: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="search">
      <Icon name="search" className="search-icon" size={14} />
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export function Segmented<T extends string>({ value, onChange, options }: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="seg" role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          className={`seg-item${value === option.value ? " active" : ""}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Splash() {
  return (
    <div className="splash">
      <div style={{ textAlign: "center" }}>
        <BrandMark size={18} />
        <div className="brand-name">HOLOXI</div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- signals

const STATE_CLASS: Record<string, string> = {
  RUNNING: "running", STARTING: "running", STOPPING: "running",
  DEGRADED: "degraded", FAILED: "failed", COMPLETED: "completed",
  STOPPED: "completed", CANCELLED: "completed", PENDING: "pending",
};

export function StateBadge({ state, reason }: { state: RunState | string; reason?: string }) {
  const tone = STATE_CLASS[state] ?? "";
  return (
    <span className={`badge ${tone}`} title={reason || undefined}>
      <span className={`led ${tone}`} />
      {state}
    </span>
  );
}

export function SourceStatus({ status }: { status: string }) {
  const tone = status === "ONLINE" ? "online" : status === "OFFLINE" ? "offline" : "";
  return (
    <span className="row" style={{ gap: 6 }}>
      <span className={`led ${tone}`} />
      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{status.toLowerCase()}</span>
    </span>
  );
}

export function Stat({ label, value, sub, tone, icon }: {
  label: string; value: ReactNode; sub?: ReactNode; tone?: "signal" | "amber" | "coral" | "mint";
  icon?: IconName;
}) {
  return (
    <div className={`card stat-card${tone ? ` tone-${tone}` : ""}`}>
      <div className="stat-head">
        <div className="stat-label">{label}</div>
        {icon && (
          <span className="stat-icon" style={tone ? { color: `var(--${tone})` } : undefined}>
            <Icon name={icon} size={14} />
          </span>
        )}
      </div>
      <div className="stat-value" style={tone ? { color: `var(--${tone})` } : undefined}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

// ------------------------------------------------------- signature strip

export interface FlowMetrics {
  [nodeId: string]: { fps?: number; rows?: number; state?: string; error?: string };
}

export function FlowStrip({ graph, apps, metrics, live, sourceName, onSelect, selected }: {
  graph: ProcessGraph;
  apps: AppManifest[];
  metrics?: FlowMetrics;
  live?: boolean;
  sourceName?: string;
  onSelect?: (nodeId: string) => void;
  selected?: string;
}) {
  const byId = new Map(apps.map((app) => [app.app_id, app]));
  const sinks = new Set<string>();
  graph.nodes.forEach((node) => node.sinks?.forEach((sink) => sinks.add(sink.kind)));
  const outputs = [...sinks];
  if (graph.nodes.some((n) => n.app_id === "raw_recorder")) outputs.push("video");
  if (graph.nodes.some((n) => ["snapshot", "dataset_collector"].includes(n.app_id))) outputs.push("images");

  return (
    <div className="flow">
      <div className="flow-node source">
        <div className="flow-kind">Source</div>
        <div className="flow-name">{sourceName ?? "HoloPod"}</div>
        <div className={`flow-metric${live ? "" : " idle"}`}>
          {live && metrics?.__source__?.fps !== undefined
            ? `${metrics.__source__.fps?.toFixed(1)} fps`
            : "stream"}
        </div>
      </div>

      {graph.nodes.map((node) => {
        const manifest = byId.get(node.app_id);
        const nodeMetrics = metrics?.[node.node_id];
        const failed = nodeMetrics?.state === "FAILED";
        return (
          <div key={node.node_id} style={{ display: "flex" }}>
            <div className={`flow-line${live && !failed ? " live" : ""}`} />
            <div
              className={`flow-node${live && !failed ? " live" : ""}${failed ? " failed" : ""}${selected === node.node_id ? " selected" : ""}`}
              onClick={() => onSelect?.(node.node_id)}
              style={onSelect ? { cursor: "pointer" } : undefined}
              role={onSelect ? "button" : undefined}
            >
              <div className="flow-kind">{manifest?.category ?? "app"}</div>
              <div className="flow-name">{node.label ?? manifest?.name ?? node.app_id}</div>
              <div className={`flow-metric${live && !failed ? "" : " idle"}`}>
                {failed
                  ? "failed"
                  : live
                    ? `${nodeMetrics?.fps?.toFixed(1) ?? "—"} fps · ${nodeMetrics?.rows ?? 0} rows`
                    : node.processing_fps
                      ? `${node.processing_fps} fps`
                      : "every frame"}
              </div>
            </div>
          </div>
        );
      })}

      {outputs.length > 0 && (
        <div style={{ display: "flex" }}>
          <div className={`flow-line${live ? " live" : ""}`} />
          <div className="flow-node sink">
            <div className="flow-kind">Storage</div>
            <div className="flow-name">{outputs.join(" · ")}</div>
            <div className="flow-metric idle">HoloXI Cloud</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------ ROI editor

export function RegionEditor({ imageUrl, regions, names, onChange, aspectLabel }: {
  imageUrl: string;
  regions: NormRect[];
  names: string[];
  onChange: (regions: NormRect[], names: string[]) => void;
  aspectLabel?: string;
}) {
  const surface = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<NormRect | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);

  const toNormalised = (event: React.MouseEvent) => {
    const box = surface.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - box.left) / box.width)),
      y: Math.min(1, Math.max(0, (event.clientY - box.top) / box.height)),
    };
  };

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="preview">
        <img src={imageUrl} alt="Source preview" />
        <div
          ref={surface}
          className="preview-overlay"
          onMouseDown={(event) => { origin.current = toNormalised(event); setDraft(null); }}
          onMouseMove={(event) => {
            if (!origin.current) return;
            const point = toNormalised(event);
            setDraft({
              x: Math.min(origin.current.x, point.x),
              y: Math.min(origin.current.y, point.y),
              width: Math.abs(point.x - origin.current.x),
              height: Math.abs(point.y - origin.current.y),
            });
          }}
          onMouseUp={() => {
            if (draft && draft.width > 0.015 && draft.height > 0.015) {
              onChange([...regions, draft], [...names, `region_${regions.length + 1}`]);
            }
            origin.current = null;
            setDraft(null);
          }}
          onMouseLeave={() => { origin.current = null; setDraft(null); }}
        >
          {regions.map((rect, index) => (
            <div
              key={index}
              className="roi"
              style={{
                left: `${rect.x * 100}%`, top: `${rect.y * 100}%`,
                width: `${rect.width * 100}%`, height: `${rect.height * 100}%`,
              }}
            >
              <span className="roi-label">{names[index] ?? `region_${index + 1}`}</span>
            </div>
          ))}
          {draft && (
            <div
              className="roi"
              style={{
                left: `${draft.x * 100}%`, top: `${draft.y * 100}%`,
                width: `${draft.width * 100}%`, height: `${draft.height * 100}%`,
              }}
            />
          )}
        </div>
        {aspectLabel && <div className="preview-badge"><span className="led online" />{aspectLabel}</div>}
      </div>

      <p className="hint">
        Drag on the image to add a region. Coordinates are stored as fractions of the frame,
        so they stay correct if the pod changes resolution.
      </p>

      {regions.length > 0 && (
        <div className="card flush">
          <table>
            <thead>
              <tr><th>Name</th><th>Position</th><th>Size</th><th /></tr>
            </thead>
            <tbody>
              {regions.map((rect, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="text"
                      value={names[index] ?? ""}
                      onChange={(event) => {
                        const next = [...names];
                        next[index] = event.target.value;
                        onChange(regions, next);
                      }}
                    />
                  </td>
                  <td className="mono">{rect.x.toFixed(3)}, {rect.y.toFixed(3)}</td>
                  <td className="mono">{rect.width.toFixed(3)} × {rect.height.toFixed(3)}</td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="btn ghost sm"
                      onClick={() => onChange(
                        regions.filter((_, i) => i !== index),
                        names.filter((_, i) => i !== index),
                      )}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------- feedback

export function Empty({ title, body, action, icon }: {
  title: string; body: string; action?: ReactNode; icon?: IconName;
}) {
  return (
    <div className="empty">
      <div className="empty-icon"><Icon name={icon ?? "inbox"} size={22} /></div>
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}

export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="stack" style={{ gap: 10 }}>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="skeleton" style={{ height: index === 0 ? 48 : 14, width: `${96 - index * 10}%` }} />
      ))}
    </div>
  );
}

export function ErrorBanner({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div className="error-banner row">
      <Icon name="warning" size={15} />
      <span>{error}</span>
      {onRetry && <button className="btn sm ghost" onClick={onRetry}>Try again</button>}
    </div>
  );
}

export function Modal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-label={title}>
        <div className="row" style={{ marginBottom: 16 }}>
          <h2>{title}</h2>
          <div className="spacer" />
          <button className="btn icon ghost sm" onClick={onClose} aria-label="Close">
            <Icon name="close" size={14} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ArtifactRow({ artifact }: { artifact: { kind: string; filename: string; size_bytes: number; rows: number | null; created_at: string; download_url: string } }) {
  return (
    <tr>
      <td><span className="tag">{artifact.kind}</span></td>
      <td className="mono">{artifact.filename}</td>
      <td className="mono">{bytes(artifact.size_bytes)}</td>
      <td className="mono">{artifact.rows ? artifact.rows.toLocaleString() : "—"}</td>
      <td style={{ color: "var(--text-muted)" }}>{relative(artifact.created_at)}</td>
      <td style={{ textAlign: "right" }}>
        <a className="btn sm" href={`${api.base}${artifact.download_url}`} download>
          <Icon name="download" size={13} />
          Download
        </a>
      </td>
    </tr>
  );
}

export { bytes, duration, relative };

/**
 * Confirmation for a destructive action.
 *
 * Typing the name is not friction for its own sake: these actions cannot be
 * undone, and the name is the one thing a person cannot supply by reflex.
 */
export function ConfirmDelete({ title, name, body, extra, confirmLabel, onConfirm, onClose }: {
  title: string;
  name: string;
  body: ReactNode;
  extra?: ReactNode;
  confirmLabel?: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal title={title} onClose={onClose}>
      <div className="stack">
        {error && <ErrorBanner error={error} />}
        <div>{body}</div>
        {extra}
        <label className="field">
          Type <span className="mono" style={{ color: "var(--text)" }}>{name}</span> to confirm
          <input type="text" value={typed} autoFocus
                 onChange={(event) => setTyped(event.target.value)} />
        </label>
        <div className="row">
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn danger"
            disabled={busy || typed.trim() !== name}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onConfirm();
                onClose();
              } catch (err) {
                setError((err as Error).message);
                setBusy(false);
              }
            }}
          >
            {busy ? "Deleting…" : confirmLabel ?? "Delete"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Delete a process with an explicit choice: configuration only, or
 * configuration plus every recorded file it produced.
 */
export function ConfirmDeleteProcess({
  name,
  versionCount,
  onConfirm,
  onClose,
}: {
  name: string;
  versionCount?: number;
  onConfirm: (purgeData: boolean) => Promise<void>;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<"process" | "all" | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const purge = mode === "all";
  const versions = versionCount && versionCount > 0 ? versionCount : 1;

  return (
    <Modal title="Delete this process?" onClose={onClose}>
      <div className="stack">
        {error && <ErrorBanner error={error} />}
        <p style={{ margin: 0 }}>
          Choose what to remove for <strong>{name}</strong>. This cannot be undone.
        </p>

        <div className="delete-choice" role="radiogroup" aria-label="What to delete">
          <button
            type="button"
            role="radio"
            aria-checked={mode === "process"}
            className={`delete-choice-option${mode === "process" ? " selected" : ""}`}
            onClick={() => setMode("process")}
          >
            <span className="delete-choice-radio" aria-hidden />
            <span>
              <span className="delete-choice-title">Just the process</span>
              <span className="hint" style={{ display: "block", marginTop: 4 }}>
                Removes the process, its {versions} version{versions === 1 ? "" : "s"}, and run
                history from HoloXI. Recorded files stay in storage at their existing paths.
              </span>
            </span>
          </button>

          <button
            type="button"
            role="radio"
            aria-checked={mode === "all"}
            className={`delete-choice-option danger${mode === "all" ? " selected" : ""}`}
            onClick={() => setMode("all")}
          >
            <span className="delete-choice-radio" aria-hidden />
            <span>
              <span className="delete-choice-title">Process and all related data</span>
              <span className="hint" style={{ display: "block", marginTop: 4 }}>
                Also permanently erases every video, image, CSV and other file this process
                produced. Admins only.
              </span>
            </span>
          </button>
        </div>

        <label className="field">
          Type <span className="mono" style={{ color: "var(--text)" }}>{name}</span> to confirm
          <input
            type="text"
            value={typed}
            autoFocus
            onChange={(event) => setTyped(event.target.value)}
          />
        </label>

        <div className="row">
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn danger"
            disabled={busy || mode === null || typed.trim() !== name}
            onClick={async () => {
              if (mode === null) return;
              setBusy(true);
              setError(null);
              try {
                await onConfirm(purge);
                onClose();
              } catch (err) {
                setError((err as Error).message);
                setBusy(false);
              }
            }}
          >
            {busy
              ? "Deleting…"
              : purge
                ? "Delete process and data"
                : "Delete process"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
